import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sparkle, Buildings, MapTrifold, Wallet, Confetti, ArrowRight, ArrowLeft,
} from "@phosphor-icons/react";

const STEPS = [
  {
    icon: Sparkle,
    title: "Welcome to IronRoot",
    body: "Your AI bookkeeping coach and business operating system. You're on a 14-day free trial — no card required. Here's the 60-second tour.",
  },
  {
    icon: Buildings,
    title: "Add your first business",
    body: "Tap “New business” to spin up a workspace. Start from a template — Wellness, E-commerce, Contractor, Real Estate — and we'll pre-load the right roadmap and tools for you.",
  },
  {
    icon: MapTrifold,
    title: "Follow the guided roadmap",
    body: "Every business gets a step-by-step setup roadmap — entity, EIN, bank accounts and clean books from day one. Each step expands into detailed sub-tasks and a pro tip.",
  },
  {
    icon: Wallet,
    title: "Track money & ask AI anything",
    body: "Snap a receipt and Rowan categorizes it, log income and expenses, watch your P&L and cash-flow runway update live, and ask Rowan anything in plain English.",
  },
  {
    icon: Confetti,
    title: "Founding member: 40% off for life",
    body: "As one of the first members, your FOUNDING40 discount is applied automatically at checkout — 40% off for life, on any plan. Explore free for 14 days, upgrade whenever you're ready.",
  },
];

const TOUR_KEY = "bk_tour_done_v1";

export function OnboardingTour({ shouldShow, onAddBusiness }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [params, setParams] = useSearchParams();
  const forceTour = params.get("tour") === "1";

  useEffect(() => {
    if (forceTour) {
      setStep(0);
      setOpen(true);
    } else if (shouldShow && !localStorage.getItem(TOUR_KEY)) {
      setOpen(true);
    }
  }, [shouldShow, forceTour]);

  const clearTourParam = () => {
    if (forceTour) {
      params.delete("tour");
      setParams(params, { replace: true });
    }
  };

  const finish = () => {
    localStorage.setItem(TOUR_KEY, "1");
    setOpen(false);
    clearTourParam();
  };

  const finishAndAdd = () => {
    finish();
    if (onAddBusiness) setTimeout(onAddBusiness, 200);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="bg-white max-w-md rounded-[28px] border-0 shadow-[0_24px_60px_rgba(26,39,68,0.18)]" aria-describedby={undefined} data-testid="onboarding-tour">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--bk-rowan-bg)" }}>
              <Icon size={26} weight="duotone" color="var(--bk-gold)" />
            </span>
            <DialogTitle className="font-display text-2xl" data-testid="tour-title">{s.title}</DialogTitle>
          </div>
        </DialogHeader>

        <p className="text-base leading-relaxed mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="tour-body">
          {s.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mt-4" data-testid="tour-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === step ? 22 : 8,
                background: i === step ? "var(--bk-accent)" : "var(--bk-border)",
              }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-5">
          {step > 0 ? (
            <button
              onClick={() => setStep((v) => v - 1)}
              data-testid="tour-back"
              className="bk-btn-secondary inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold"
              style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}
            >
              <ArrowLeft size={16} weight="bold" /> Back
            </button>
          ) : (
            <button
              onClick={finish}
              data-testid="tour-skip"
              className="px-4 py-2.5 rounded-full text-sm font-semibold"
              style={{ color: "var(--bk-text-2)" }}
            >
              Skip tour
            </button>
          )}

          {isLast ? (
            <button
              onClick={finishAndAdd}
              data-testid="tour-finish"
              className="bk-btn-primary inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold"
            >
              Add my first business <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <button
              onClick={() => setStep((v) => v + 1)}
              data-testid="tour-next"
              className="bk-btn-primary inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold"
            >
              Next <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
